"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, ChevronUp, Copy, Check } from "lucide-react";
import { ApiError } from "@/lib/api-error";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ApiErrorContextValue {
  showError: (error: unknown) => void;
}

const ApiErrorContext = createContext<ApiErrorContextValue | null>(null);

export function useApiError() {
  const ctx = useContext(ApiErrorContext);
  if (!ctx) throw new Error("useApiError must be used within ApiErrorProvider");
  return ctx;
}

interface ErrorState {
  kind: "network" | "request" | "unexpected";
  debugMessage: string;
  response: unknown;
  status?: number;
  method?: string;
  endpoint?: string;
}

export function ApiErrorProvider({ children }: { children: ReactNode }) {
  const t = useTranslations("ApiError");
  const [error, setError] = useState<ErrorState | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [copied, setCopied] = useState(false);

  const showError = useCallback((err: unknown) => {
    if (err instanceof ApiError) {
      setError({
        kind: "request",
        debugMessage: err.message,
        response: err.response,
        status: err.status,
        method: err.method,
        endpoint: err.endpoint,
      });
    } else if (err instanceof Error) {
      setError({
        kind: err instanceof TypeError ? "network" : "unexpected",
        debugMessage: err.message,
        response: null,
      });
    } else {
      setError({
        kind: "unexpected",
        debugMessage: String(err),
        response: null,
      });
    }
    setShowDebug(false);
    setCopied(false);
  }, []);

  const handleClose = useCallback(() => {
    setError(null);
    setShowDebug(false);
    setCopied(false);
  }, []);

  const buildDebugText = useCallback(() => {
    if (!error) return "";
    const parts: string[] = [];
    if (error.debugMessage) {
      parts.push(error.debugMessage);
    }
    if (error.method || error.endpoint) {
      parts.push(`${error.method ?? "?"} ${error.endpoint ?? "?"}`);
    }
    if (error.status != null) {
      parts.push(`Status: ${error.status}`);
    }
    if (error.response != null) {
      parts.push(JSON.stringify(error.response, null, 2));
    }
    return parts.join("\n");
  }, [error]);

  const hasDebugInfo = import.meta.env.DEV && !!error && (
    !!error.debugMessage ||
    !!error.method ||
    !!error.endpoint ||
    error.status != null ||
    error.response != null
  );

  const handleCopy = useCallback(async () => {
    const text = buildDebugText();
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [buildDebugText]);

  return (
    <ApiErrorContext.Provider value={{ showError }}>
      {children}
      <Dialog open={!!error} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("title")}</DialogTitle>
            <DialogDescription>
              {error?.kind === "network"
                ? t("networkError")
                : error?.kind === "request"
                  ? t("requestError")
                  : t("unexpectedError")}
            </DialogDescription>
          </DialogHeader>

          {hasDebugInfo && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowDebug((v) => !v)}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm transition-colors"
              >
                {showDebug ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
                {t("debugInfo")}
                {error.status != null && (
                  <span className="bg-muted text-muted-foreground ml-1 rounded px-1.5 py-0.5 font-mono text-xs">
                    {error.status}
                  </span>
                )}
              </button>

              {showDebug && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 size-7"
                    onClick={handleCopy}
                    aria-label={copied ? t("copiedDebug") : t("copyDebug")}
                  >
                    {copied ? (
                      <Check className="size-3.5" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </Button>
                  <pre className="bg-muted max-h-64 overflow-auto rounded-md p-3 font-mono text-xs">
                    {buildDebugText()}
                  </pre>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              {t("close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ApiErrorContext.Provider>
  );
}
